import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Header } from 'components/common/header/Header';
import { Button } from 'components/common/button/Button';
import './TimePicker.css';

export const TimePicker = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [period, setPeriod] = useState<'오전' | '오후'>('오후');
    const [hour, setHour] = useState(6);
    const [minute, setMinute] = useState(0);

    const hours = Array.from({ length: 12 }, (_, i) => i + 1);
    const minutes = Array.from({ length: 12 }, (_, i) => i * 5); // 5분 단위: 0, 5, 10, ..., 55

    const hourScrollRef = useRef<HTMLDivElement>(null);
    const minuteScrollRef = useRef<HTMLDivElement>(null);
    const hourScrollTimer = useRef<NodeJS.Timeout | null>(null);
    const minuteScrollTimer = useRef<NodeJS.Timeout | null>(null);

    const handleConfirm = () => {
        const selectedTime = `${period} ${hour}:${minute.toString().padStart(2, '0')}`;
        // returnTo가 있으면 해당 경로로, 없으면 기본 step2로 이동
        const returnTo = location.state?.returnTo || '/meeting/create/step2';

        // 이전 state를 보존하면서 선택한 시간만 업데이트
        // 시간 선택 후 TimePicker로 돌아갈 필요 없으므로 replace 사용
        navigate(returnTo, {
            state: {
                ...location.state,
                selectedTime
            },
            replace: true
        });
    };

    const scrollToCenter = (value: number, type: 'hour' | 'minute') => {
        const element = document.getElementById(`${type}-${value}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    // 가장 가까운 중앙 아이템 찾기
    const findClosestItem = (scrollContainer: HTMLDivElement, items: number[], type: 'hour' | 'minute') => {
        const containerRect = scrollContainer.getBoundingClientRect();
        const containerCenter = containerRect.top + containerRect.height / 2;

        let closestItem = items[0];
        let minDistance = Infinity;

        items.forEach(item => {
            const element = document.getElementById(`${type}-${item}`);
            if (element) {
                const itemRect = element.getBoundingClientRect();
                const itemCenter = itemRect.top + itemRect.height / 2;
                const distance = Math.abs(itemCenter - containerCenter);

                if (distance < minDistance) {
                    minDistance = distance;
                    closestItem = item;
                }
            }
        });

        return closestItem;
    };

    // 시간 스크롤 핸들러
    const handleHourScroll = () => {
        if (hourScrollTimer.current) {
            clearTimeout(hourScrollTimer.current);
        }

        hourScrollTimer.current = setTimeout(() => {
            if (hourScrollRef.current) {
                const closestHour = findClosestItem(hourScrollRef.current, hours, 'hour');
                setHour(closestHour);
                scrollToCenter(closestHour, 'hour');
            }
        }, 150); // 스크롤 멈춘 후 150ms 대기
    };

    // 분 스크롤 핸들러
    const handleMinuteScroll = () => {
        if (minuteScrollTimer.current) {
            clearTimeout(minuteScrollTimer.current);
        }

        minuteScrollTimer.current = setTimeout(() => {
            if (minuteScrollRef.current) {
                const closestMinute = findClosestItem(minuteScrollRef.current, minutes, 'minute');
                setMinute(closestMinute);
                scrollToCenter(closestMinute, 'minute');
            }
        }, 150); // 스크롤 멈춘 후 150ms 대기
    };

    // 초기 위치 설정
    useEffect(() => {
        scrollToCenter(hour, 'hour');
        scrollToCenter(minute, 'minute');
    }, []);

    return (
        <div className="time-picker-wrapper">
            <Header title="시간 선택" isSubPage />

            <div className="time-picker-content">
                <div className="time-picker-title">시간을 선택해 주세요</div>

                {/* Period Toggle (AM/PM) */}
                <div className="period-toggle">
                    <button
                        className={`period-button ${period === '오전' ? 'active' : ''}`}
                        onClick={() => setPeriod('오전')}
                    >
                        오전
                    </button>
                    <button
                        className={`period-button ${period === '오후' ? 'active' : ''}`}
                        onClick={() => setPeriod('오후')}
                    >
                        오후
                    </button>
                </div>

                {/* Time Picker Wheels */}
                <div className="time-picker-container">
                    <div className="time-picker-overlay-top"></div>
                    <div className="time-picker-selected-indicator"></div>
                    <div className="time-picker-overlay-bottom"></div>

                    <div className="time-picker-wheels">
                        {/* Hour Picker */}
                        <div className="picker-column">
                            <div
                                className="picker-scroll"
                                ref={hourScrollRef}
                                onScroll={handleHourScroll}
                            >
                                {hours.map((h) => (
                                    <div
                                        key={h}
                                        id={`hour-${h}`}
                                        className={`picker-item ${hour === h ? 'selected' : ''}`}
                                        onClick={() => {
                                            setHour(h);
                                            scrollToCenter(h, 'hour');
                                        }}
                                    >
                                        {h}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="picker-separator">:</div>

                        {/* Minute Picker */}
                        <div className="picker-column">
                            <div
                                className="picker-scroll"
                                ref={minuteScrollRef}
                                onScroll={handleMinuteScroll}
                            >
                                {minutes.map((m) => (
                                    <div
                                        key={m}
                                        id={`minute-${m}`}
                                        className={`picker-item ${minute === m ? 'selected' : ''}`}
                                        onClick={() => {
                                            setMinute(m);
                                            scrollToCenter(m, 'minute');
                                        }}
                                    >
                                        {m.toString().padStart(2, '0')}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Selected Time Display */}
                <div className="selected-time-display">
                    <span className="selected-time-text">
                        {period} {hour}:{minute.toString().padStart(2, '0')}
                    </span>
                </div>
            </div>

            {/* Confirm Button */}
            <div className="time-picker-footer">
                <Button text="확인" onPress={handleConfirm} />
            </div>
        </div>
    );
};
